/**
 * A análise completa da formalização, por expansão (E152.F3, ADR-0103 §5.9): os requisitos da
 * leitura que sustenta, as rotas de atribuição, os elementos de prova com a fonte e o alcance, as
 * diligências (decisivas e de reforço), os achados e a conferência. Tudo vem do servidor, na ordem
 * dele.
 */
import type { ReactNode } from 'react';

import { Badge } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import {
  leituraEscolhida,
  type AvaliacaoDaFormalizacaoData,
  type ResultadoDaFormalizacaoData,
} from '@/lib/formalizacaoAvaliacao';

import { AchadosDaFormalizacao } from './AchadosDaFormalizacao';

const t = ptBR.formalizacaoAvaliacao;

function Bloco({ titulo, children }: { titulo: string; children: ReactNode }): JSX.Element {
  return (
    <section className="space-y-sc-2">
      <h5 className="text-body-sm font-semibold text-fg">{titulo}</h5>
      {children}
    </section>
  );
}

function Faltam({ itens }: { itens: string[] }): JSX.Element | null {
  if (itens.length === 0) return null;
  return <p className="text-fg-muted">{t.falta.replace('{itens}', itens.join('; '))}</p>;
}

function Requisitos({ a }: { a: AvaliacaoDaFormalizacaoData }): JSX.Element | null {
  const leitura = leituraEscolhida(a);
  if (!leitura) return null;
  return (
    <Bloco titulo={t.requisitos}>
      <ul className="space-y-sc-2 text-body-sm">
        {leitura.requisitos.map((r) => (
          <li key={r.requisito_id} data-testid="formalizacao-requisito-avaliado">
            <p className="text-fg">
              {r.titulo} <Badge>{t.situacaoDoRequisito[r.situacao]}</Badge>
            </p>
            <p className="text-fg-muted">{r.motivo}</p>
            <Faltam itens={r.faltam} />
          </li>
        ))}
      </ul>
    </Bloco>
  );
}

export function AnaliseCompleta({
  resultado,
}: {
  resultado: ResultadoDaFormalizacaoData;
}): JSX.Element {
  const a = resultado.avaliacao;
  return (
    <details className="border border-border p-sc-3" data-testid="formalizacao-analise-completa">
      <summary className="cursor-pointer text-body-md text-fg">{t.detalhes}</summary>
      <div className="mt-sc-3 space-y-sc-4">
        <Requisitos a={a} />
        <Bloco titulo={t.rotas}>
          <ul className="space-y-sc-2 text-body-sm">
            {a.rotas.map((r) => (
              <li key={r.rota} data-testid="formalizacao-rota" data-rota={r.rota}>
                <p className="text-fg">
                  {r.rota} <Badge>{t.situacaoDaRota[r.situacao]}</Badge>
                </p>
                <p className="text-fg-muted">{r.motivo}</p>
                <Faltam itens={r.faltam} />
              </li>
            ))}
          </ul>
        </Bloco>
        <Bloco titulo={t.elementos}>
          <ul className="space-y-sc-2 text-body-sm">
            {a.elementos.map((e) => (
              <li key={e.id} data-testid="formalizacao-elemento" data-situacao={e.situacao}>
                <p className="text-fg">
                  {t.tipoDoElemento[e.tipo]} <Badge>{t.situacaoDoElemento[e.situacao]}</Badge>
                </p>
                <p className="text-fg-muted">{e.alcance}</p>
                <p className="text-caption text-fg-dim">{e.fonte}</p>
              </li>
            ))}
          </ul>
        </Bloco>
        <Bloco titulo={t.diligencias}>
          {a.diligencias.length === 0 ? (
            <p className="text-body-sm text-fg-muted">{t.semDiligencias}</p>
          ) : (
            <ul className="space-y-sc-1 text-body-sm">
              {a.diligencias.map((d) => (
                <li key={d.id} data-testid="formalizacao-diligencia">
                  <Badge variant={d.decisiva ? 'warning' : 'neutral'}>
                    {d.decisiva ? t.decisiva : t.reforco}
                  </Badge>{' '}
                  <span className="text-fg">{d.descricao}</span>
                </li>
              ))}
            </ul>
          )}
        </Bloco>
        <AchadosDaFormalizacao resultado={resultado} />
      </div>
    </details>
  );
}
